import { Mutation, Action } from 'vuex-module-decorators';
import { Resolve } from 'vue-di';
import { InjectModule, InjectVuexModule } from 'vue-di/vuex';
import { EdgePatientEnrollmentApi } from '@trialcard/apigateway.client/edgePatientEnrollmentApi';
import {
    EnrollPatientRequestModel,
    EnterpriseCoordinatorAccountUpsearchRequestModel,
    UpsearchModelEnterpriseCoordinatorPatientServiceModelsPatientUpsearchModel,
    UpsearchModelEnterpriseCoordinatorAccountServiceModelsAccountUpsearchModel,
    EnterpriseCoordinatorPatientServiceModelsPatientUpsearchModel,
    EnterpriseCoordinatorSharedAddressUpsearchModel,
    EnterpriseCoordinatorSharedPhoneUpsearchModel,
    GetBestEnrollmentV2RequestModel,
    EnterpriseCoordinatorPrescriberServiceModelsPrescriberLicenseUpsearchModel,
    EnterpriseCoordinatorSharedExternalIdUpsearchModel,
    EnterpriseCoordinatorSharedEmailAddressUpsearchModel,
    EnterpriseCoordinatorPrescriberServiceModelsPrescriberUpsearchModel,
    UpsearchModelEnterpriseCoordinatorPrescriberServiceModelsPrescriberUpsearchModel,
    EnterpriseCoordinatorSiteServiceModelsSiteUpsearchModel,
    UpsearchModelEnterpriseCoordinatorSiteServiceModelsSiteUpsearchModel,
} from '@trialcard/apigateway.models';
import { Gender, RoleType } from '@trialcard/enums';

import { Moment } from 'moment';
import { AxiosResponse } from 'axios';
import { EdgeEnrollmentApi } from '@trialcard/apigateway.client';
import SessionModule from './session';
import { OnClear } from '~/services/clearModule';
import { Settings } from '~/services/settings';

import SurveyModule from '~/store/survey';

@InjectModule({ stateFactory: true }, module)
@OnClear<EnrollmentModule>(async value => value.clear())
export default class EnrollmentModule extends InjectVuexModule {
    @Resolve
    public patientEnrollmentApi!: EdgePatientEnrollmentApi;

    @Resolve
    public enrollmentApi!: EdgeEnrollmentApi;

    @Resolve
    public settings!: Settings;

    @Resolve
    public survey!: SurveyModule;

    @Resolve
    public session!: SessionModule;

    alternativeEnrollment: boolean | null = null;

    memberNumber: string | null = null;

    expirationDate: string | null = null;

    // mapping to EnterpriseCoordinatorPatientServiceModelsPatientUpsearchModel

    defaultPatientInformationData = {
        firstName: '' as string,
        lastName: '' as string,
        dateOfBirth: null as Moment | null,
        addresses: [] as EnterpriseCoordinatorSharedAddressUpsearchModel[],
        phoneNumbers: [] as EnterpriseCoordinatorSharedPhoneUpsearchModel[],
        emailAddresses: [] as EnterpriseCoordinatorSharedEmailAddressUpsearchModel[],
        gender: null as Gender | null,
    };

    defaultPrescriberInformationData = {
        accountRelationshipIsPrimary: true,
        firstName: '',
        middleName: '',
        lastName: '',
        suffix: '',
        nickName: '',
        npi: '',
        taxId: '',
        prescriberLicenses: [] as EnterpriseCoordinatorPrescriberServiceModelsPrescriberLicenseUpsearchModel[],
        externalIds: [] as EnterpriseCoordinatorSharedExternalIdUpsearchModel[],
        addresses: [] as EnterpriseCoordinatorSharedAddressUpsearchModel[],
        phoneNumbers: [] as EnterpriseCoordinatorSharedPhoneUpsearchModel[],
        emailAddresses: [] as EnterpriseCoordinatorSharedEmailAddressUpsearchModel[],
    } as EnterpriseCoordinatorPrescriberServiceModelsPrescriberUpsearchModel;

    defaultSiteData = [
        {
            model: {
                accountRelationshipIsPrimary: true,
                siteRoleType: RoleType.PrescriberSite,
                pointOfContacts: [],
                description: '',
                addresses: [
                    {
                        addressOne: '',
                        addressTwo: '',
                        zip: '',
                        city: '',
                        state: '',
                    },
                ],
                contacts: [],
                emailAddresses: [],
                externalIds: [],
                phoneNumbers: [],
                globalIds: [],
            },
        },
    ] as UpsearchModelEnterpriseCoordinatorSiteServiceModelsSiteUpsearchModel[];

    patientInformationData = { ...this.defaultPatientInformationData };
    prescriberInformationData = { ...this.defaultPrescriberInformationData };
    siteData = { ...this.defaultSiteData };
    externalId = '';

    @Mutation
    public setEnrollmentType(alternative: boolean) {
        this.alternativeEnrollment = alternative;
    }

    @Mutation
    public async updatePatientInformation(newPatientInformation: typeof EnrollmentModule.prototype.patientInformationData) {
        this.patientInformationData = { ...this.patientInformationData, ...newPatientInformation };
    }

    @Mutation
    public async updatePrescriberInformation(newPrescriberInformation: typeof EnrollmentModule.prototype.prescriberInformationData) {
        this.prescriberInformationData = { ...this.prescriberInformationData, ...newPrescriberInformation };
    }

    @Mutation
    public async updateSiteData(newSiteData: UpsearchModelEnterpriseCoordinatorSiteServiceModelsSiteUpsearchModel[]) {
        this.siteData = [...newSiteData];
    }

    @Mutation
    public async updateExternalId(newExternalId: string) {
        this.externalId = newExternalId;
    }

    @Mutation
    public clear() {}

    @Mutation
    public setMemberNumber(memberNumber: string | null) {
        this.memberNumber = memberNumber;
    }

    @Mutation
    public setExpirationDate(expirationDate: Moment) {
        this.expirationDate = expirationDate.format('L');
    }

    @Action({ rawError: true })
    public clearEnrollment() {
        this.updatePatientInformation({ ...this.defaultPatientInformationData });
        this.setMemberNumber(null);
    }

    @Action({ rawError: true })
    public async checkForExistingEnrollments() {
        const patient: GetBestEnrollmentV2RequestModel = {
            firstName: this.patientInformationData.firstName as string,
            lastName: this.patientInformationData.lastName as string,
            dateOfBirth: this.patientInformationData.dateOfBirth as Moment,
            zip: this.patientInformationData.addresses[0].zip as string,
            gender: this.patientInformationData.gender as Gender,
        };
        const response = await this.enrollmentApi.enrollmentGetBestEnrollmentsV2(patient, this.settings.programId);
        return (response && response.data && response.data.data && response.data.data) || [];
    }

    @Action({ rawError: true })
    public async submitToAPI() {
        let response = null;
        const externalIds = this.externalId
            ? [
                  {
                      externalIdType: 7,
                      externalId: this.externalId,
                  },
              ]
            : [];
        try {
            const patient = {
                account: {
                    patientData: {
                        canUpdate: true,
                        canInsert: true,
                        skipSearch: false,
                        model: {
                            ...this.patientInformationData,
                            middleName: '',
                            suffix: '',
                            nickname: '',
                        } as EnterpriseCoordinatorPatientServiceModelsPatientUpsearchModel,
                    } as UpsearchModelEnterpriseCoordinatorPatientServiceModelsPatientUpsearchModel,
                    accountData: {
                        canUpdate: true,
                        canInsert: true,
                        skipSearch: false,
                        model: {
                            accountType: 1,
                            accountStatus: 1,
                            externalIds,
                        },
                    } as UpsearchModelEnterpriseCoordinatorAccountServiceModelsAccountUpsearchModel,
                    prescriberData: [
                        {
                            canUpdate: true,
                            canInsert: true,
                            skipSearch: false,
                            model: {
                                ...this.prescriberInformationData,
                            } as EnterpriseCoordinatorPrescriberServiceModelsPrescriberUpsearchModel,
                        },
                    ] as UpsearchModelEnterpriseCoordinatorPrescriberServiceModelsPrescriberUpsearchModel[],
                    siteData: this.siteData,
                    surveySessionData: [this.survey.sessionId] as number[],
                } as EnterpriseCoordinatorAccountUpsearchRequestModel,
            } as EnrollPatientRequestModel;

            const existingEnrollment = await this.checkForExistingEnrollments();
            if (Array.isArray(existingEnrollment) && existingEnrollment.length > 0) {
                const number =
                    (existingEnrollment[0].memberNumbers &&
                        existingEnrollment[0].memberNumbers[0] &&
                        existingEnrollment[0].memberNumbers[0].number) ||
                    '';
                const endDate = (existingEnrollment[0] && existingEnrollment[0].endDate) || '';
                response = { status: 200, data: { data: { enrollment: { memberNumbers: [{ number }], endDate } } } };
            } else {
                response = await this.patientEnrollmentApi.patientEnrollmentEnrollPatient(patient, this.settings.programId);
            }
        } catch {
            response = { status: 400 } as AxiosResponse;
        }
        return response;
    }
}
